import { useState } from "react";
import { Calculator as CalcIcon, Info } from "lucide-react";
import { SectionHeading } from "./SectionHeading";

const FUELS = [
  { id: "petrol", label: "Бензин" },
  { id: "diesel", label: "Дизель" },
  { id: "electric", label: "Електро" },
];

const eur = (n) =>
  new Intl.NumberFormat("uk-UA", { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));

const compute = ({ fuel, cc, kwh, year, value }) => {
  const cur = new Date().getFullYear();
  let age = cur - year;
  if (!Number.isFinite(age) || age < 0) age = 0;
  let ageCoef = age < 1 ? 1 : age;
  if (ageCoef > 15) ageCoef = 15;

  let duty = 0;
  let excise = 0;
  let vat = 0;

  if (fuel === "electric") {
    // Електромобілі: мито 0%, ПДВ — пільга, акциз 1 € за 1 кВт·год
    excise = 1 * (kwh || 0);
  } else {
    duty = value * 0.1; // мито 10%
    let base;
    if (fuel === "petrol") base = cc <= 3000 ? 50 : 100;
    else base = cc <= 3500 ? 75 : 150; // diesel
    excise = base * ((cc || 0) / 1000) * ageCoef;
    vat = (value + duty + excise) * 0.2; // ПДВ 20%
  }

  return { duty, excise, vat, total: duty + excise + vat, ageCoef };
};

const fieldCls =
  "w-full rounded-md border border-white/15 bg-[#0a0a0a] px-4 py-3 text-white outline-none transition-colors focus:border-[#B12A2A]";

export const CustomsCalculator = () => {
  const [fuel, setFuel] = useState("petrol");
  const [cc, setCc] = useState("1968");
  const [kwh, setKwh] = useState("60");
  const [year, setYear] = useState("2019");
  const [value, setValue] = useState("12000");
  const [result, setResult] = useState(null);

  const onCalc = () => {
    setResult(
      compute({
        fuel,
        cc: Number(cc) || 0,
        kwh: Number(kwh) || 0,
        year: Number(year) || 0,
        value: Number(value) || 0,
      })
    );
  };

  const isEv = fuel === "electric";

  return (
    <section id="calculator" className="border-t border-white/5 bg-[#0a0a0a] py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="Калькулятор розмитнення"
          title="Порахуйте вартість розмитнення авто"
          subtitle="Орієнтовний розрахунок митних платежів в Україні: мито, акциз та ПДВ. Введіть дані автомобіля та митну вартість."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          {/* Inputs */}
          <div className="rounded-md border border-white/10 bg-[#121212] p-6 sm:p-8" data-testid="calculator-form">
            <div className="mb-5">
              <label className="mb-2 block text-sm font-medium text-white/70">Тип двигуна</label>
              <div className="grid grid-cols-3 gap-2">
                {FUELS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFuel(f.id)}
                    data-testid={`fuel-${f.id}`}
                    className={`rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${
                      fuel === f.id
                        ? "border-[#B12A2A] bg-[#B12A2A] text-white"
                        : "border-white/15 text-white/70 hover:border-white/30"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {isEv ? (
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/70">
                    Ємність батареї (кВт·год)
                  </label>
                  <input
                    type="number"
                    value={kwh}
                    onChange={(e) => setKwh(e.target.value)}
                    data-testid="input-kwh"
                    className={fieldCls}
                    placeholder="60"
                  />
                </div>
              ) : (
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/70">
                    Об'єм двигуна (см³)
                  </label>
                  <input
                    type="number"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    data-testid="input-cc"
                    className={fieldCls}
                    placeholder="1968"
                  />
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-medium text-white/70">Рік випуску</label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  data-testid="input-year"
                  className={fieldCls}
                  placeholder="2019"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm font-medium text-white/70">
                  Митна вартість (€)
                </label>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="input-value"
                  className={fieldCls}
                  placeholder="12000"
                />
              </div>
            </div>

            <button
              onClick={onCalc}
              data-testid="calculate-button"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#B12A2A] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#C73D3A]"
            >
              <CalcIcon className="h-5 w-5" />
              Розрахувати
            </button>
          </div>

          {/* Result */}
          <div className="flex flex-col rounded-md border border-white/10 bg-[#121212] p-6 sm:p-8" data-testid="calculator-result">
            <h3 className="font-heading text-xl font-semibold text-white">Орієнтовний розрахунок</h3>

            {result ? (
              <div className="mt-6 flex-1">
                <Row label="Мито (10%)" value={result.duty} testid="result-duty" />
                <Row label="Акциз" value={result.excise} testid="result-excise" />
                <Row label="ПДВ (20%)" value={result.vat} testid="result-vat" />
                <div className="mt-4 flex items-baseline justify-between border-t border-white/10 pt-5">
                  <span className="text-lg font-semibold text-white">Разом до сплати</span>
                  <span className="font-heading text-3xl font-bold text-[#B12A2A]" data-testid="result-total">
                    € {eur(result.total)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center py-10 text-center text-white/40">
                Заповніть дані та натисніть «Розрахувати»
              </div>
            )}

            <div className="mt-6 flex items-start gap-2 rounded-md bg-white/5 p-4 text-xs leading-relaxed text-white/50">
              <Info className="mt-0.5 h-4 w-4 flex-none text-[#B12A2A]" />
              <span>
                Розрахунок виконано за методикою Держмитслужби (мито + акциз + ПДВ) і є
                орієнтовним — точну суму визначає митниця. Допоможу з підготовкою документів
                та розмитненням особисто.
              </span>
            </div>
          </div>
        </div>

        {/* Customs valuation & legal support */}
        <div className="mt-8 rounded-md border border-[#B12A2A]/30 bg-[#B12A2A]/5 p-6 sm:p-8" data-testid="valuation-note">
          <h3 className="font-heading text-xl font-semibold text-white sm:text-2xl">
            Якщо митниця оцінить авто дорожче?
          </h3>
          <p className="mt-4 text-base leading-relaxed text-white/70">
            Митниця{" "}
            <span className="text-white">не завжди приймає ціну з договору купівлі-продажу</span>. Часто
            вартість порівнюється з ринковими базами (Eurotax, Schwacke, mobile.de, auto.ria) і може бути
            визначена вище за вашу ціну — тоді й платежі зростають.
          </p>
          <p className="mt-3 text-base leading-relaxed text-white/70">
            У такому разі <span className="text-white">я допомагаю</span>: готую всі документи правильно ще
            від початку процесу, щоб у разі завищеної оцінки все було готове навіть для суду.
          </p>
          <p className="mt-3 text-base leading-relaxed text-white/70">
            За практикою, якщо всі документи в порядку, шанс виграти спір у суді —
            <span className="font-semibold text-[#B12A2A]"> близько 99%</span>. Ви не залишаєтесь із цією
            проблемою наодинці.
          </p>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between border-b border-white/5 py-3">
    <span className="text-white/70">{label}</span>
    <span className="font-semibold text-white" data-testid={testid}>
      € {eur(value)}
    </span>
  </div>
);
